import axios from 'axios'
import { client } from "@/plugins/axios";

let _token: Token | null = null

export interface KeyValuePair {
    [key: string]: string | null | number | boolean | KeyValuePair | KeyValuePair[] | number[] | string[];
}

export enum SignatureConnectionMode {
    Wamp = 2
}

/**
 * Máquina registrada en Unigest
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class Machine {
    public name = ''
    public description = ''
    public signatureConnectionMode: SignatureConnectionMode = SignatureConnectionMode.Wamp

    /**
     * Crea una entidad a partir de la información de la API
     * @param data
     */
    public static deserialize (data: KeyValuePair) {
        return Object.assign(new this(), {
            name: data.Nombre,
            description: data.Descripcion,
        })
    }
}

export class Token {
    public token = ''
    public expiresIn = 0
    public type = 'bearer'
    public context = {
        session_id: '',
        session_cookie_name: '',
        api_url: '',
        api_version: '',
        app_release: '',
    }

    public static fromAppRouter (data: KeyValuePair) {
        return Object.assign(new this(), {
            token: data.token,
            expiresIn: data.expires_in,
            type: data.type,
            context: data.context,
        })
    }

    public static fromUnigest (data: KeyValuePair) {
        return Object.assign(new this(), {
            token: data.access_token,
            expiresIn: data.expires_in,
            type: data.token_type,
        })
    }
}

/**
 * Parámetros de configuración del usuario registrados en Unigest
 */
export class UserConfig {
    /**
     * Numero de serie de la instalación de Unigest
     */
    serial = ''

    public static deserialize (data: KeyValuePair) {
        return Object.assign(new this(), {
            serial: data.numeroDeSerie,
        })
    }
}

/**
 * Solicita un token de autenticación al enrutador de aplicaciones de Artisan
 *
 * @param user
 * @param password
 */
async function tokenFromAppRouter (user: string, password: string) {
    const http = axios.create({
        baseURL: 'https://beta.unigest.net/api/v1'
    })

    //const test = "test";
    const response = await http.post('/app-router/auth/token', {
    //const response = await http.post('/token', {
        user, password
    })

    return Token.fromAppRouter(response.data)
}

/**
 * Solicita un token de autenticación directamente en el servicio de Unigest
 *
 * @param user
 * @param password
 */
async function tokenFromUnigest (user: string, password: string) {
    const response = await client.post('/token', {
        user, password
    })

    return Token.fromUnigest(response.data)
}

/**
 * Obtiene un token de autorización de Unigest
 *
 * @param user
 * @param password
 */
export async function token (user: string, password: string) {
    const isDevelopment = process.env.NODE_ENV === 'development'

    return _token = isDevelopment
        ? await tokenFromUnigest(user, password)
        : await tokenFromAppRouter(user, password)
}


/**
 * Permite obtener la lista de maquinas registrada en Unigest
 */
export async function getMachines () {
    const response = await client.post('/Ficheros/ObtenerEntidadesPaginado', {
        nombreEntidad: 'maquinas',
        numRegistros: 40,
        direccion: 1,
        nombreCampoOrdenacion: 'nombre',
        ultimoValorBuscado: 1,
        camposLike: '',
        valueLike: '',
        queContengaPalabras: false,
        ascendente: true,
        obtenerFoto: false,
        esSoloConStock: false,
        idFamiliaAsociada: '',
        obtenerPVPIVA: false
    })

    const items = response.data.entidades || []

    return items.map((item: KeyValuePair) => Machine.deserialize(item))
}

/**
 * Obtiene la configuración del usuario
 */
export async function getUserConfig (user: string, password: string) {
    const response = await client.get('/Account/Iniciar', {
        params: { email: user, password },
    })

    return UserConfig.deserialize(response.data)
}

/**
 * Carga el token actual
 */
export function loadToken (): Token | null {
    return _token
}
